import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider, useApp } from "./context/AppContext";
import { Toast } from "./components/common/UI";
import DashboardLayout from "./components/layout/DashboardLayout";

import LoginPage from "./pages/auth/LoginPage";
import ExplorePage from "./components/common/Explore/ExplorePage";
import ConsentManagementPage from "./components/common/Explore/ConsentManagementPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import TenantsPage from "./pages/admin/TenantsPage";
import {
  UsersOverviewPage,
  LicensesPage,
  AdminSettingsPage,
} from "./pages/admin/AdminSubPages";
import {
  TenantDashboard,
  TenantUsersPage,
  TenantLicensePage,
  TenantSettingsPage,
} from "./pages/tenant/TenantPages";
import AdminEmailTemplatesPage from "./pages/admin/AdminEmailTemplatesPage";
import TenantEmailTemplatesPage from "./pages/tenant/TenantEmailTemplatesPage";
import TenantPolicyPromptPageModule from "./pages/tenant/TenantPolicyPromptPage";
import EmailActivityLogs from "./components/common/EmailActivityLogs";
import DepartmentsPage from "./pages/tenant/DepartmentsPage";
import FormQuestionsManager from "./pages/tenant/FormQuestionsManager";
import GapAssessmentAdmin from "./pages/tenant/GapAssessmentAdmin";
import ModuleAssessmentAdmin from "./pages/tenant/ModuleAssessmentAdmin";
import FormResponsesMonitor from "./pages/tenant/FormResponsesMonitor";
import AuditLogsPage from "./pages/tenant/AuditLogsPage";
import UserHome from "./pages/user/UserHome";
// import HeadDepartmentPage from "./pages/user/HeadDepartmentPage";
import UserDashboard from "./pages/user/UserDashboard";
import GapAssessmentPage from "./pages/user/GapAssessment";
import GapAssessmentSubmissions from "./pages/user/GapAssessmentSubmissions";
import {
  PIASubmissions,
  ROPASubmissions,
  ROPAAutomatedSubmissions,
  TPRMSubmissions,
} from "./components/common/FormSubmissionsPage";
import PIA from "./pages/user/PIA";
import ROPA, { RopaCoverPage } from "./pages/user/ROPA";
import BreachManagement from "./pages/user/BreachManagement";
import ConsentVariance from "./pages/user/ConsentVariance";
import DSARRecords from "./pages/user/DSARRecords";
import PolicyStore from "./pages/user/PolicyStore";
import Reports from "./pages/user/Reports";
import TPRM from "./pages/user/TPRM";
import AssetTracker from "./pages/user/AssetTracker";
import GlobalCompliance from "./pages/user/GlobalCompliance";
import HelpGuidePage from "./pages/help/HelpGuidePage";
import { LiveTourProvider } from "./context/LiveTourContext";
import ComplianceChecklist from "./pages/user/ComplianceChecklist";
import AdminComplianceChecklist from "./pages/admin/AdminComplianceChecklist";
import TenantComplianceChecklist from "./pages/tenant/TenantComplianceChecklist";
import LawQuestionMappingStudio from "./pages/tenant/LawQuestionMappingStudio";

const TenantPolicyPromptPage =
  typeof TenantPolicyPromptPageModule === "function"
    ? TenantPolicyPromptPageModule
    : TenantPolicyPromptPageModule?.default;

function ProtectedRoute({ children, allowedRoles, requiredModule }) {
  const { currentUser } = useApp();
  if (!currentUser) return <Navigate to="/login" replace />;

  const homeFor = (role) =>
    role === "admin" ? "/admin" : role === "tenant_admin" ? "/tenant" : "/user";

  if (allowedRoles && !allowedRoles.includes(currentUser.role)) {
    return <Navigate to={homeFor(currentUser.role)} replace />;
  }

  if (requiredModule) {
    const allowedModules = currentUser.modules || [];
    if (!allowedModules.includes(requiredModule)) {
      return <Navigate to={homeFor(currentUser.role)} replace />;
    }
  }

  return <DashboardLayout>{children}</DashboardLayout>;
}

function RootRedirect() {
  const { currentUser } = useApp();
  if (!currentUser) return <ExplorePage />;
  if (currentUser.role === "admin") return <Navigate to="/admin" replace />;
  if (currentUser.role === "tenant_admin")
    return <Navigate to="/tenant" replace />;
  return <Navigate to="/user" replace />;
}

function AppRoutes() {
  const { notification } = useApp();
  return (
    <>
      <Toast notification={notification} />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/explore" element={<ExplorePage />} />
        <Route path="/consent-management" element={<ConsentManagementPage />} />
        <Route path="/" element={<RootRedirect />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/compliance-checklists"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminComplianceChecklist />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/tenants"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <TenantsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <UsersOverviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/licenses"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <LicensesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/email-templates"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminEmailTemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/email-logs"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <EmailActivityLogs apiBase="/admin/settings" isPlatform={true} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <TenantDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/users"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <TenantUsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/departments"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <DepartmentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/questionnaire"
          element={<Navigate to="/tenant/form-questions/tprm" replace />}
        />
        <Route
          path="/tenant/form-questions"
          element={
            <Navigate to="/tenant/form-questions/gap_assessment" replace />
          }
        />
        <Route
          path="/tenant/form-questions/:formKey"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <FormQuestionsManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/form-responses"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <FormResponsesMonitor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/gap-assessment"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <GapAssessmentAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/pia-tracking"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <ModuleAssessmentAdmin key="pia" module="pia" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/tprm-tracking"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <ModuleAssessmentAdmin key="tprm" module="tprm" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/audit-logs"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <AuditLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/license"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <TenantLicensePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/settings"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <TenantSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/compliance-checklists"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <TenantComplianceChecklist />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/compliance-checklists/map"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <LawQuestionMappingStudio />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/email-templates"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <TenantEmailTemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/policy-prompt"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              {typeof TenantPolicyPromptPage === "function" ? (
                <TenantPolicyPromptPage />
              ) : (
                <div className="p-6 text-sm text-slate-600">
                  Policy prompt page failed to load. Refresh the browser.
                </div>
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/email-logs"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <EmailActivityLogs
                apiBase="/tenant/settings"
                isPlatform={false}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <UserHome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/dashboard"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <UserDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/global-compliance"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <GlobalCompliance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/global-compliance/checklist"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ComplianceChecklist />
            </ProtectedRoute>
          }
        />
        {/* <Route
          path="/user/head-department"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <HeadDepartmentPage />
            </ProtectedRoute>
          }
        /> */}
        <Route
          path="/user/gap-assessment"
          element={
            <ProtectedRoute allowedRoles={["user"]} requiredModule="gap">
              <GapAssessmentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/gap-assessment/submissions"
          element={
            <ProtectedRoute allowedRoles={["user"]} requiredModule="gap">
              <GapAssessmentSubmissions />
            </ProtectedRoute>
          }
        />

        <Route
          path="/user/pia"
          element={
            <ProtectedRoute allowedRoles={["user"]} requiredModule="dpia">
              <PIA />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/pia/submissions"
          element={
            <ProtectedRoute allowedRoles={["user"]} requiredModule="dpia">
              <PIASubmissions />
            </ProtectedRoute>
          }
        />

        <Route
          path="/user/ropa"
          element={
            <ProtectedRoute allowedRoles={["user"]} requiredModule="ropa">
              <RopaCoverPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/ropa/manual"
          element={
            <ProtectedRoute allowedRoles={["user"]} requiredModule="ropa">
              <ROPA />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/ropa/automated"
          element={
            <ProtectedRoute allowedRoles={["user"]} requiredModule="ropa">
              <ROPAAutomatedSubmissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/ropa/submissions"
          element={
            <ProtectedRoute allowedRoles={["user"]} requiredModule="ropa">
              <ROPASubmissions />
            </ProtectedRoute>
          }
        />

        <Route
          path="/user/breach-management"
          element={
            <ProtectedRoute allowedRoles={["user"]} requiredModule="breach">
              <BreachManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/user/consent-variance"
          element={
            <ProtectedRoute allowedRoles={["user"]} requiredModule="consent">
              <ConsentVariance />
            </ProtectedRoute>
          }
        />

        <Route
          path="/user/dsar"
          element={
            <ProtectedRoute allowedRoles={["user"]} requiredModule="dsar">
              <DSARRecords />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/dsar_records"
          element={<Navigate to="/user/dsar" replace />}
        />

        <Route
          path="/user/policy-store"
          element={
            <ProtectedRoute
              allowedRoles={["user"]}
              requiredModule="policy_store"
            >
              <PolicyStore />
            </ProtectedRoute>
          }
        />

        <Route
          path="/user/reports"
          element={
            <ProtectedRoute allowedRoles={["user"]} requiredModule="reports">
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/tprm"
          element={
            <ProtectedRoute allowedRoles={["user"]} requiredModule="tprm">
              <TPRM />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/tprm/submissions"
          element={
            <ProtectedRoute allowedRoles={["user"]} requiredModule="tprm">
              <TPRMSubmissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/asset-tracker"
          element={
            <ProtectedRoute
              allowedRoles={["user"]}
              requiredModule="asset_tracker"
            >
              <AssetTracker />
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute allowedRoles={["admin", "tenant_admin", "user"]}>
              <HelpGuidePage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <LiveTourProvider>
          <AppRoutes />
        </LiveTourProvider>
      </BrowserRouter>
    </AppProvider>
  );
}
